import React, { useEffect, useState } from 'react';
import { ExpandLessRounded, ExpandMoreRounded, PsychologyAltRounded } from '@mui/icons-material';
import { ClassEntryAssessment, getClassEntryAssessment } from '../../services/api';

interface Props {
  token: string;
  school: string;
  grade: number;
  section: string | null;
}

// One violet ramp, light -> dark, lowest level first. Matches the monitor.
const LEVEL_COLORS: Record<string, { bg: string; chip: string }> = {
  emerging: { bg: '#DDD6FE', chip: 'bg-violet-100 text-violet-700' },
  developing: { bg: '#A78BFA', chip: 'bg-violet-200 text-violet-800' },
  curious: { bg: '#7C3AED', chip: 'bg-violet-600 text-white' },
  highly_curious: { bg: '#4C1D95', chip: 'bg-violet-900 text-white' },
};

const fmt = (n?: number | null) => (n == null ? '—' : String(n));

/** The class's results on the one-time curiosity assessment: the mix of
 *  curiosity levels, the class average per part, what students are wondering
 *  about, and each student's own result. */
const EntryAssessmentCard: React.FC<Props> = ({ token, school, grade, section }) => {
  const [data, setData] = useState<ClassEntryAssessment | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    getClassEntryAssessment(token, school, grade, section)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [token, school, grade, section]);

  if (data && !data.required) return null;

  const questions = data
    ? data.students.filter((s) => s.curious_question).slice(0, 6)
    : [];

  return (
    <section className="mb-2 rounded-2xl border border-[#E9E6F4] bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <PsychologyAltRounded fontSize="small" className="text-violet-500" />
        <div>
          <h2 className="text-base font-bold text-slate-900">Curiosity assessment</h2>
          <p className="text-xs text-slate-500">
            What each student told us about their curiosity, once, before their first chat with the coach
          </p>
        </div>
      </div>

      {error ? (
        <p className="text-sm text-rose-600">{error}</p>
      ) : !data ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-slate-50 p-3">
              <p className="text-2xl font-bold text-slate-900">
                {data.completed}
                <span className="text-base font-medium text-slate-400">/{data.total_students}</span>
              </p>
              <p className="text-xs text-slate-500">Students who have taken it</p>
            </div>
            <div className="rounded-xl bg-slate-50 p-3">
              <p className="text-2xl font-bold text-slate-900">
                {fmt(data.average_score)}
                <span className="text-base font-medium text-slate-400">/100</span>
              </p>
              <p className="text-xs text-slate-500">Average curiosity score</p>
            </div>
          </div>

          {data.completed > 0 && (
            <>
              {/* Level mix */}
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Curiosity levels</p>
              <div className="mb-2 flex h-3 overflow-hidden rounded-full bg-slate-100">
                {data.levels
                  .filter((l) => l.count > 0)
                  .map((l) => (
                    <div
                      key={l.key}
                      title={`${l.label}: ${l.count}`}
                      style={{ width: `${(l.count * 100) / data.completed}%`, background: LEVEL_COLORS[l.key]?.bg }}
                      className="border-r-2 border-white last:border-r-0"
                    />
                  ))}
              </div>
              <div className="mb-5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
                {data.levels.map((l) => (
                  <span key={l.key} className="flex items-center gap-1.5">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: LEVEL_COLORS[l.key]?.bg }} />
                    {l.label} <span className="font-semibold text-slate-900">{l.count}</span>
                  </span>
                ))}
              </div>

              {/* Per part */}
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">Class average by part</p>
              <div className="mb-5 flex flex-col gap-2">
                {data.sections.map((s) => (
                  <div key={s.id} className="grid grid-cols-[minmax(0,10rem)_1fr_2.5rem] items-center gap-3 text-sm">
                    <span className="truncate text-slate-700">{s.label}</span>
                    <div className="h-2 overflow-hidden rounded-full bg-violet-100">
                      <div className="h-full rounded-full bg-violet-600" style={{ width: `${s.score ?? 0}%` }} />
                    </div>
                    <span className="text-right font-semibold tabular-nums text-slate-900">{fmt(s.score)}</span>
                  </div>
                ))}
              </div>

              {/* What they're wondering about */}
              {questions.length > 0 && (
                <div className="mb-5">
                  <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    What your students are wondering about
                  </p>
                  <ul className="flex flex-col gap-1.5">
                    {questions.map((s) => (
                      <li key={s.student_id} className="rounded-lg bg-violet-50 px-3 py-2 text-sm text-slate-800">
                        “{s.curious_question}”
                        <span className="ml-1 text-xs text-slate-500">— {s.name || 'Student'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {data.students.length === 0 ? (
            <p className="text-sm text-slate-400">No students in this class yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {data.students.map((s) => (
                <li key={s.student_id} className="py-2">
                  <button
                    className="flex w-full items-center justify-between gap-3 text-left text-sm disabled:cursor-default"
                    disabled={!s.completed}
                    onClick={() => setOpen(open === s.student_id ? null : s.student_id)}
                  >
                    <span className="text-slate-800">
                      {s.name || 'Student'}
                      {s.roll_number != null && <span className="ml-1 text-slate-400">#{s.roll_number}</span>}
                    </span>
                    {s.completed ? (
                      <span className="flex items-center gap-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                            LEVEL_COLORS[s.curiosity_level || '']?.chip || 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {s.level_label}
                        </span>
                        <span className="w-8 text-right font-semibold tabular-nums text-slate-900">
                          {fmt(s.curiosity_score)}
                        </span>
                        {open === s.student_id ? (
                          <ExpandLessRounded fontSize="small" className="text-slate-400" />
                        ) : (
                          <ExpandMoreRounded fontSize="small" className="text-slate-400" />
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">Not taken yet</span>
                    )}
                  </button>
                  {open === s.student_id && (
                    <div className="mt-2 rounded-lg bg-slate-50 p-3">
                      <div className="flex flex-col gap-1.5">
                        {s.section_scores.map((sec) => (
                          <div key={sec.id} className="grid grid-cols-[minmax(0,9rem)_1fr_2rem] items-center gap-2 text-xs">
                            <span className="truncate text-slate-600">{sec.label}</span>
                            <div className="h-1.5 overflow-hidden rounded-full bg-violet-100">
                              <div className="h-full rounded-full bg-violet-500" style={{ width: `${sec.score ?? 0}%` }} />
                            </div>
                            <span className="text-right tabular-nums text-slate-700">{fmt(sec.score)}</span>
                          </div>
                        ))}
                      </div>
                      {s.curious_question && (
                        <p className="mt-2.5 text-xs text-slate-700">
                          <span className="font-semibold">Their question:</span> “{s.curious_question}”
                        </p>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
};

export default EntryAssessmentCard;
